import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Eye, Upload, FileText, Globe, EyeOff, Link2, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3 py-2 text-sm text-ei-graphite focus:border-ei-navy outline-none";
const labelCls = "block text-xs font-display font-bold text-ei-navy mb-1.5";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  category: "Battery basics",
  author: "EI Battery Solutions",
  seo_title: "",
  meta_description: "",
  image: "",
  image_alt: "",
  sources: [],
  related_slugs: [],
  product_slugs: [],
};

export default function GuidesAdmin() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const fileRef = useRef(null);

  const loadWebhook = async () => {
    try {
      const { data } = await api.get("/staff/guides/webhook");
      setWebhook(data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load the Holo connection"));
    }
  };

  const rotateToken = async () => {
    if (!window.confirm("Issue a new token? Holo will stop sending until you paste the new one in.")) return;
    try {
      await api.post("/staff/guides/webhook/rotate");
      await loadWebhook();
      toast.success("New token issued — update it in Holo");
    } catch (e) {
      toast.error(formatApiError(e, "Could not issue a new token"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/guides");
      setArticles(data.articles || []);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load the guides"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selected = useMemo(() => articles.find((a) => a.id === selectedId) || null, [articles, selectedId]);

  const edit = (article) => {
    setSelectedId(article.id);
    setForm({
      title: article.title || "",
      slug: article.slug || "",
      excerpt: article.excerpt || "",
      body: article.body || "",
      category: article.category || "Battery basics",
      author: article.author || "EI Battery Solutions",
      seo_title: article.seo_title || "",
      meta_description: article.meta_description || "",
      image: article.image || "",
      image_alt: article.image_alt || "",
      sources: article.sources || [],
      related_slugs: article.related_slugs || [],
      product_slugs: article.product_slugs || [],
    });
  };

  const startNew = () => {
    setSelectedId(null);
    setForm(EMPTY);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setList = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean) }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Give the article a title first");
      return;
    }
    setBusy(true);
    try {
      const { data } = selectedId
        ? await api.put(`/staff/guides/${selectedId}`, form)
        : await api.post("/staff/guides", form);
      toast.success(selectedId ? "Guide saved" : "Draft created");
      await load();
      edit(data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not save this guide"));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (article, status) => {
    setBusy(true);
    try {
      await api.post(`/staff/guides/${article.id}/status`, { status });
      toast.success(status === "published" ? "Guide published" : "Guide unpublished");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not change the status"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Permanently delete "${article.title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/staff/guides/${article.id}`);
      if (selectedId === article.id) startNew();
      toast.success("Guide deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not delete this guide"));
    } finally {
      setBusy(false);
    }
  };

  const importMarkdown = async () => {
    if (markdown.trim().length < 10) {
      toast.error("Paste the article Markdown first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/guides/import", { markdown });
      toast.success("Imported as a draft");
      setMarkdown("");
      setImportOpen(false);
      await load();
      edit(data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not import this Markdown"));
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/staff/guides/image", body, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e, "Could not upload this image"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="guides-admin">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-display font-bold tracking-widest text-ei-golddark uppercase">EI Battery Guides</div>
          <h1 className="mt-1 font-display text-2xl font-bold text-ei-navy">Guides</h1>
          <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">
            Write, preview and publish the public guides at eibattery.com.au/guides. Imported articles always arrive as drafts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setWebhookOpen((v) => !v); if (!webhook) loadWebhook(); }} className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist bg-white px-4 py-2 rounded-sm hover:border-ei-navy min-h-[40px]" data-testid="guides-webhook-toggle">
            <Link2 className="w-3.5 h-3.5" /> Holo connection
          </button>
          <button onClick={() => setImportOpen((v) => !v)} className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist bg-white px-4 py-2 rounded-sm hover:border-ei-navy min-h-[40px]" data-testid="guides-import-toggle">
            <FileText className="w-3.5 h-3.5" /> Import Markdown
          </button>
          <button onClick={startNew} className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-white bg-ei-navy px-4 py-2 rounded-sm hover:bg-ei-graphite min-h-[40px]" data-testid="guides-new">
            <Plus className="w-3.5 h-3.5" /> New guide
          </button>
        </div>
      </div>

      {webhookOpen && (
        <div className="mt-5 bg-white border border-ei-mist rounded-md p-5" data-testid="guides-webhook-panel">
          <h2 className="font-display text-sm font-bold text-ei-navy">Send articles straight from Holo</h2>
          <p className="text-xs text-ei-graphite/65 mt-1 max-w-3xl leading-relaxed">
            Point Holo's webhook at the address below and add the token as a header. Every article arrives as a
            draft — nothing is ever published automatically. Re-sending the same article updates its draft.
          </p>
          {!webhook ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-ei-graphite/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Webhook address</label>
                  <input readOnly value={webhook.url} onFocus={(e) => e.target.select()} className={`${inputCls} font-mono text-xs`} data-testid="guides-webhook-url" />
                </div>
                <div>
                  <label className={labelCls}>Header name</label>
                  <input readOnly value={webhook.header} onFocus={(e) => e.target.select()} className={`${inputCls} font-mono text-xs`} data-testid="guides-webhook-header" />
                </div>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Token (paste this into Holo)</label>
                <div className="flex flex-wrap gap-2">
                  <input readOnly value={webhook.token} onFocus={(e) => e.target.select()} className={`${inputCls} font-mono text-xs flex-1 min-w-[260px]`} data-testid="guides-webhook-token" />
                  <button onClick={rotateToken} className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist px-4 py-2 rounded-sm hover:border-ei-navy min-h-[40px]" data-testid="guides-webhook-rotate">
                    <RefreshCw className="w-3.5 h-3.5" /> New token
                  </button>
                </div>
              </div>
              <div className="mt-5">
                <div className="text-xs font-display font-bold tracking-widest text-ei-graphite/50 uppercase">Recent deliveries</div>
                {webhook.deliveries.length === 0 ? (
                  <p className="text-sm text-ei-graphite/60 mt-2" data-testid="guides-webhook-empty">Nothing received from Holo yet.</p>
                ) : (
                  <ul className="mt-2 space-y-1" data-testid="guides-webhook-deliveries">
                    {webhook.deliveries.map((d, i) => (
                      <li key={`${d.received_at}-${i}`} className="text-xs text-ei-graphite/70 flex flex-wrap gap-2">
                        <span className="tnum">{(d.received_at || "").slice(0, 16).replace("T", " ")}</span>
                        <span className={d.accepted ? "text-green-700" : "text-red-700"}>{d.accepted ? "accepted" : "rejected"}</span>
                        <span>{d.title || d.reason}</span>
                        {d.slug && <span className="font-mono">/guides/{d.slug}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {importOpen && (
        <div className="mt-5 bg-white border border-ei-mist rounded-md p-5" data-testid="guides-import-panel">
          <label className={labelCls}>Paste Markdown (front matter supported: title, slug, excerpt, category, author)</label>
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={8}
            className={`${inputCls} font-mono text-xs`}
            placeholder={"---\ntitle: kW vs kWh\ncategory: Battery basics\n---\n\n## Power versus capacity..."}
            data-testid="guides-import-textarea"
          />
          <button onClick={importMarkdown} disabled={busy} className="mt-3 inline-flex items-center gap-2 font-display text-xs font-bold text-ei-white bg-ei-navy px-4 py-2 rounded-sm hover:bg-ei-graphite min-h-[40px] disabled:opacity-50" data-testid="guides-import-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Import as draft
          </button>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-3" data-testid="guides-admin-list">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ei-navy/40" /></div>
          ) : articles.length === 0 ? (
            <p className="text-sm text-ei-graphite/60">No guides yet.</p>
          ) : (
            articles.map((article) => (
              <div
                key={article.id}
                className={`bg-white border rounded-md p-4 ${selectedId === article.id ? "border-ei-navy" : "border-ei-mist"}`}
                data-testid={`guides-admin-item-${article.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => edit(article)} className="text-left min-w-0" data-testid={`guides-edit-${article.slug}`}>
                    <div className="font-display text-sm font-bold text-ei-navy leading-snug">{article.title}</div>
                    <div className="mt-1 text-xs text-ei-graphite/55">/guides/{article.slug}</div>
                  </button>
                  <span
                    className={`shrink-0 text-[11px] font-display font-bold px-2 py-1 rounded-sm ${
                      article.status === "published" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                    }`}
                    data-testid={`guides-status-${article.slug}`}
                  >
                    {article.status === "published" ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={`/guides/${article.slug}?preview=${article.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-ei-navy border border-ei-mist px-2.5 py-1.5 rounded-sm hover:border-ei-navy"
                    data-testid={`guides-preview-${article.slug}`}
                  >
                    <Eye className="w-3 h-3" /> Preview
                  </a>
                  {article.status === "published" ? (
                    <button onClick={() => setStatus(article, "draft")} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-amber-700 border border-amber-200 px-2.5 py-1.5 rounded-sm hover:bg-amber-50 disabled:opacity-50" data-testid={`guides-unpublish-${article.slug}`}>
                      <EyeOff className="w-3 h-3" /> Unpublish
                    </button>
                  ) : (
                    <button onClick={() => setStatus(article, "published")} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-green-700 border border-green-200 px-2.5 py-1.5 rounded-sm hover:bg-green-50 disabled:opacity-50" data-testid={`guides-publish-${article.slug}`}>
                      <Globe className="w-3 h-3" /> Publish
                    </button>
                  )}
                  <button onClick={() => remove(article)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-red-700 border border-red-200 px-2.5 py-1.5 rounded-sm hover:bg-red-50 disabled:opacity-50" data-testid={`guides-delete-${article.slug}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="lg:col-span-8 bg-white border border-ei-mist rounded-md p-5" data-testid="guides-editor">
          <h2 className="font-display text-sm font-bold text-ei-navy">
            {selectedId ? `Editing: ${selected?.title || form.title}` : "New guide"}
          </h2>

          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title</label>
              <input value={form.title} onChange={set("title")} className={inputCls} data-testid="guide-field-title" />
            </div>
            <div>
              <label className={labelCls}>Slug (leave blank to generate)</label>
              <input value={form.slug} onChange={set("slug")} className={inputCls} placeholder="kw-vs-kwh" data-testid="guide-field-slug" />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={set("category")} className={inputCls} data-testid="guide-field-category">
                {(categories.length ? categories : [form.category]).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Excerpt</label>
              <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={inputCls} data-testid="guide-field-excerpt" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Body (Markdown)</label>
              <textarea value={form.body} onChange={set("body")} rows={16} className={`${inputCls} font-mono text-xs`} data-testid="guide-field-body" />
            </div>
            <div>
              <label className={labelCls}>Author</label>
              <input value={form.author} onChange={set("author")} className={inputCls} data-testid="guide-field-author" />
            </div>
            <div>
              <label className={labelCls}>SEO title</label>
              <input value={form.seo_title} onChange={set("seo_title")} className={inputCls} data-testid="guide-field-seo-title" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Meta description</label>
              <textarea value={form.meta_description} onChange={set("meta_description")} rows={2} className={inputCls} data-testid="guide-field-meta-description" />
            </div>
            <div>
              <label className={labelCls}>Cover image</label>
              <div className="flex gap-2">
                <input value={form.image} onChange={set("image")} className={inputCls} placeholder="/api/guides/images/..." data-testid="guide-field-image" />
                <button onClick={() => fileRef.current?.click()} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-display font-bold text-ei-navy border border-ei-mist px-3 rounded-sm hover:border-ei-navy min-h-[44px] disabled:opacity-50" data-testid="guide-upload-image">
                  <Upload className="w-3.5 h-3.5" /> Upload
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="guide-image-input" />
            </div>
            <div>
              <label className={labelCls}>Image alt text</label>
              <input value={form.image_alt} onChange={set("image_alt")} className={inputCls} data-testid="guide-field-image-alt" />
            </div>
            <div>
              <label className={labelCls}>Source references (one per line)</label>
              <textarea value={form.sources.join("\n")} onChange={setList("sources")} rows={3} className={inputCls} data-testid="guide-field-sources" />
            </div>
            <div>
              <label className={labelCls}>Related guide slugs (one per line)</label>
              <textarea value={form.related_slugs.join("\n")} onChange={setList("related_slugs")} rows={3} className={inputCls} data-testid="guide-field-related" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Product page slugs to link (one per line, e.g. power-stack-pro-100)</label>
              <textarea value={form.product_slugs.join("\n")} onChange={setList("product_slugs")} rows={2} className={inputCls} data-testid="guide-field-products" />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-white bg-ei-navy px-5 py-2.5 rounded-sm hover:bg-ei-graphite min-h-[44px] disabled:opacity-50" data-testid="guide-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {selectedId ? "Save changes" : "Create draft"}
            </button>
            {selectedId && (
              <a href={`/guides/${selected?.slug}?preview=${selectedId}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist px-5 py-2.5 rounded-sm hover:border-ei-navy min-h-[44px]" data-testid="guide-preview-current">
                <Eye className="w-3.5 h-3.5" /> Preview
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
